'use client';
import { Mobile } from '@components/Mobile';
import { MediaQuery } from '@constants/theme';
import { ImgProps } from '@custom-types/ImgProps';
import { Modal, ModalProps, styled } from '@mui/material';
import MuiBox from '@new-components/atoms/MuiBox';
import MuiTypography from '@new-components/atoms/MuiTypography';
import {
  ReactSlickCarouselType,
  SlickCarousel,
} from '@new-components/atoms/SlickCarousel';
import { Desktop } from '@tesareeh-components/common/Desktop';
import Image from 'next/image';
import React, { useEffect } from 'react';

const CloseIcon = () => (
  <svg
    width='18'
    height='18'
    viewBox='0 0 18 18'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M14.25 3.74988L3.75 14.2499M3.75 3.74988L14.25 14.2499'
      stroke='white'
      strokeWidth='1.25'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const PrevArrowIcon = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M5 12L20 11.9998'
      stroke='white'
      strokeWidth='1.25'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M9 6.99988L4.70711 11.2928C4.37377 11.6261 4.20711 11.7928 4.20711 11.9999C4.20711 12.207 4.37377 12.3737 4.70711 12.707L9 16.9999'
      stroke='white'
      strokeWidth='1.25'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const NextArrowIcon = () => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M19 11.9998L4 11.9998'
      stroke='white'
      strokeWidth='1.25'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M15 6.99988L19.2929 11.2928C19.6262 11.6261 19.7929 11.7928 19.7929 11.9999C19.7929 12.207 19.6262 12.3737 19.2929 12.707L15 16.9999'
      stroke='white'
      strokeWidth='1.25'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ImageTrackCarousel = styled(SlickCarousel)(({ theme }) => ({
  '.slick-track': {
    display: 'flex !important',
  },
  '.slick-slide': {
    'cursor': 'pointer',
    'opacity': 0.5,
    'transition': 'opacity 0.2s ease-in-out',
    '&:hover': {
      opacity: 0.8,
    },
  },
  '.slick-current': {
    'opacity': 1,
    '&:hover': {
      opacity: '1 !important',
    },
  },
  '.slick-arrow': {
    display: 'none !important',
  },
}));
const SelectedImageCarousel = styled(SlickCarousel)(({ theme }) => ({
  '.slick-arrow': {
    display: 'none !important',
  },
  '.slick-slider': {
    height: '100% !important',
  },
  'slick-list': {
    height: '100% !important',
  },
  '.slick-track': {
    height: '100% !important',
  },
}));

interface ImageCountProps {
  current: number;
  total: number;
}
const ImageCount = React.memo(function ImageCount(props: ImageCountProps) {
  return (
    <MuiBox
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '10px',
      }}>
      <MuiTypography
        sx={{
          fontSize: 15,
          fontWeight: 400,
          lineHeight: '18.75px',
          letterSpacing: '-0.4000000059604645px',
          textAlign: 'center',
          color: 'rgba(255,255,255,1)',
        }}>
        {props.current} of {props.total}
      </MuiTypography>
    </MuiBox>
  );
});
interface PhoneGalleryProps extends Pick<ModalProps, 'open' | 'onClose'> {
  images: ImgProps[];
  initialIndex?: number;
}
const PhotoGallery: React.FC<PhoneGalleryProps> = (props) => {
  const mainCarouselRef = React.useRef<ReactSlickCarouselType>(null);
  const trackCarouselRef = React.useRef<ReactSlickCarouselType>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(props.initialIndex || 0);
  const totalSlideCount = React.useMemo(() => {
    return props.images.length;
  }, [props.images]);

  const handleNext = () => {
    mainCarouselRef.current?.slickNext();
  };
  const handlePrev = () => {
    mainCarouselRef.current?.slickPrev();
  };

  const handleImageClick = (index: number) => () => {
    const trackElem = trackCarouselRef.current;
    const mainElem = mainCarouselRef.current;
    if (trackElem) {
      trackElem.slickGoTo(index);
      mainElem?.slickGoTo(index);
    }
  };
  const handleMainCarouselNavigation = (current: number, next: number) => {
    const mainElem = mainCarouselRef.current;
    const trackElem = trackCarouselRef.current;
    if (mainElem) {
      mainElem.slickGoTo(next);
    }
    if (trackElem) {
      trackElem.slickGoTo(next);
    }
    setCurrentSlideIndex(next);
  };

  const handleCloseButtonClick = () => {
    if (props.onClose) props.onClose({}, 'backdropClick');
  };
  //Effect to focus the initialIndex on opening the gallery
  useEffect(() => {
    if (props.open) {
      setTimeout(() => handleImageClick(props.initialIndex || 0)(), 500);
    }
  }, [props.open]);

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      sx={{
        width: '100%',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
      }}
      slotProps={{
        backdrop: {
          sx: {
            background: 'rgba(0,0,0,.85)',
          },
        },
      }}>
      <MuiBox
        sx={{
          flex: 1,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          paddingY: '22px',
          paddingX: 0,
          position: 'relative',
          [MediaQuery.tablet]: {
            paddingY: '60px',
            paddingX: '100px',
            paddingBottom: '30px',
          },
        }}>
        <Mobile>
          <ImageCount current={currentSlideIndex + 1} total={totalSlideCount} />
        </Mobile>
        <MuiBox
          onClick={handleCloseButtonClick}
          component={'button'}
          sx={{
            'position': 'absolute',
            'top': '16px',
            'right': '16px',
            'cursor': 'pointer',
            'minWidth': 32,
            'minHeight': 32,
            'gap': 4,
            'borderRadius': '100px',
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'background': 'rgba(89, 89, 89, .80)',
            'backgroundBlendMode': 'overlay',
            'backdropFilter': 'blur(80px)',
            'border': 'none',
            '&:hover': {
              background: 'rgba(89, 89, 89, 1)',
            },
            [MediaQuery.tablet]: {
              top: '32px',
              right: '32px',
            },
          }}>
          <CloseIcon />
        </MuiBox>
        <MuiBox
          sx={{
            flex: 1,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
          }}>
          <MuiBox
            data-testid={'prev-button'}
            disabled={currentSlideIndex === 0}
            data-disabled={currentSlideIndex === 0}
            component={'button'}
            onClick={handlePrev}
            sx={{
              'minWidth': 40,
              'minHeight': 40,
              'gap': 4,
              'cursor': 'pointer',
              'display': 'none',
              'alignItems': 'center',
              'justifyContent': 'center',
              'borderRadius': '100px',
              'background': 'rgba(89, 89, 89, .80)',
              'backgroundBlendMode': 'overlay',
              'backdropFilter': 'blur(80px)',
              'border': 'none',
              '&:hover': {
                background: 'rgba(89, 89, 89, 1)',
                transform: 'scale(1.05)',
              },
              [MediaQuery.tablet]: {
                display: 'flex',
              },
              ['&:disabled']: {
                opacity: 0.5,
                cursor: 'unset',
                background: 'rgba(89, 89, 89, 1)',
                transform: 'scale(1.05)',
              },
            }}>
            <PrevArrowIcon />
          </MuiBox>
          <MuiBox
            sx={{
              flex: 1,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}>
            <SelectedImageCarousel
              ref={mainCarouselRef}
              slidesToShow={1}
              initialSlide={props.initialIndex || 0}
              swipeToSlide
              infinite={false}
              beforeChange={handleMainCarouselNavigation}>
              {props.images.map((image, index) => (
                <MuiBox
                  key={index}
                  data-testid={`gallery-img-${index}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    paddingX: '16px',
                  }}>
                  <Image
                    src={image.src}
                    alt={image.alt}
                    style={{
                      objectFit: 'contain',
                      borderRadius: '16px',
                      width: '100%',
                      border: 'none',
                    }}
                    placeholder='blur'
                    blurDataURL='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAwAB/oegShIAAAAASUVORK5CYII='
                    width={1120}
                    height={608}
                  />
                </MuiBox>
              ))}
            </SelectedImageCarousel>
          </MuiBox>
          <MuiBox
            disabled={currentSlideIndex + 1 === totalSlideCount}
            data-disabled={currentSlideIndex + 1 === totalSlideCount}
            data-testid={'next-button'}
            component={'button'}
            onClick={handleNext}
            sx={{
              'minWidth': 40,
              'minHeight': 40,
              'gap': 4,
              'borderRadius': '100px',
              'cursor': 'pointer',
              'display': 'none',
              'alignItems': 'center',
              'justifyContent': 'center',
              'background': 'rgba(89, 89, 89, .80)',
              'backgroundBlendMode': 'overlay',
              'backdropFilter': 'blur(80px)',
              'border': 'none',
              '&:hover': {
                background: 'rgba(89, 89, 89, 1)',
                transform: 'scale(1.05)',
              },
              [MediaQuery.tablet]: {
                display: 'flex',
              },
              ['&:disabled']: {
                opacity: 0.5,
                cursor: 'unset',
                background: 'rgba(89, 89, 89, 1)',
                transform: 'scale(1.05)',
              },
            }}>
            <NextArrowIcon />
          </MuiBox>
        </MuiBox>
        <MuiBox sx={{ height: '150px', width: '100%' }}>
          <ImageTrackCarousel
            slidesToShow={10}
            infinite={false}
            ref={trackCarouselRef}
            initialSlide={props.initialIndex || 0}
            swipeToSlide
            responsive={[
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: 6,
                },
              },
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: 4,
                },
              },
            ]}>
            {props.images.map((image, index) => (
              <MuiBox
                onClick={handleImageClick(index)}
                key={index}
                data-testid={`track-img-${index}`}
                sx={{
                  position: 'relative',
                  height: '100px',
                  width: '100%',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  paddingX: '8px',
                }}>
                <Image
                  src={image.src}
                  alt={image.alt}
                  width={150}
                  height={150}
                  style={{ objectFit: 'contain', width: '100%', height: '100%' }}
                  placeholder='blur'
                  blurDataURL='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAwAB/oegShIAAAAASUVORK5CYII='
                />
              </MuiBox>
            ))}
          </ImageTrackCarousel>
          <Desktop>
            <ImageCount
              current={currentSlideIndex + 1}
              total={totalSlideCount}
            />
          </Desktop>
        </MuiBox>
      </MuiBox>
    </Modal>
  );
};

export default PhotoGallery;
